import { useState, useEffect } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../firebase/firebase';

export const useEventStatus = (eventId?: string) => {
    // Starts closed, not open: the status arrives on its own listener, and an
    // event that defaulted to "open" announced itself as active for a frame
    // before the real value landed — on a closed event that read as a wrong badge.
    const [isClosed, setIsClosed] = useState<boolean>(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!eventId) {
            setLoading(false);
            return;
        }

        const eventListRef = ref(database, `eventList/${eventId}`);
        const unsubscribe = onValue(
            eventListRef,
            (snapshot) => {
                try {
                    const isActive = snapshot.val();
                    setIsClosed(!isActive);
                    setError(null);
                } catch (err) {
                    setError('Failed to load event status');
                    console.error('Error loading event status:', err);
                } finally {
                    setLoading(false);
                }
            },
            (err) => {
                setError('Failed to load event status');
                setLoading(false);
                console.error('Firebase error:', err);
            }
        );

        return () => unsubscribe();
    }, [eventId]);

    const closeEvent = async () => {
        if (!eventId) return;
        const eventListRef = ref(database, `eventList/${eventId}`);
        await set(eventListRef, false); // false = closed
    };

    const reopenEvent = async () => {
        if (!eventId) return;
        const eventListRef = ref(database, `eventList/${eventId}`);
        await set(eventListRef, true); // true = active
    };

    return { isClosed, loading, error, closeEvent, reopenEvent };
};